import { useCallback } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useAnalytics } from "~/contexts/analytics-context";
import { Skeleton } from "~/components/skeleton";
import { Text } from "~/components/text";
import { type Granularity, EmptyChart } from "~/components/charts";
import { useAmazonSales } from "~/pages/product-details/tabs/sales-data-tab/hooks/use-amazon-sales";
import type { MetadataEvent } from "~/pages/product-details/tabs/sales-data-tab/hooks/use-metadata-events";
import { useBundleFormatMeta } from "~/pages/product-details/tabs/sales-data-tab/data/use-bundle-format-meta";
import { AmazonSalesChart } from "./amazon-sales-chart";
import { AmazonSalesChartPanel } from "./amazon-sales-chart-panel";

export const AmazonSales = ({
  isbns,
  bookTitle,
  publishedAt,
  isBundled = false,
  bundleIsbns = [],
  workId,
  currentIsbn,
  events,
}: {
  isbns: string[];
  bookTitle: string;
  publishedAt?: Date | null;
  isBundled?: boolean;
  bundleIsbns?: string[];
  workId?: string | null;
  currentIsbn?: string;
  events?: MetadataEvent[];
}) => {
  const { t } = useTranslation(["productDetails"]);
  const { gaEvent } = useAnalytics();
  const { formats, isFormatsLoading } = useBundleFormatMeta({
    isBundled,
    workId,
    bundleIsbns,
    currentIsbn,
  });

  const queryIsbns = isBundled && bundleIsbns.length > 0 ? bundleIsbns : isbns;

  const {
    sales,
    isLoading,
    isError,
    granularity,
    setGranularity,
    availableGranularities,
  } = useAmazonSales(queryIsbns);

  const handleGranularityChange = useCallback(
    (next: Granularity) => {
      setGranularity(next);
      gaEvent({
        type: "sales_amazon_sales_granularity",
        payload: { granularity: next, isbn: isbns[0] },
      });
    },
    [gaEvent, isbns, setGranularity],
  );

  if (isError) {
    return (
      <div className="rounded-xl border border-border bg-background p-6 text-center">
        <Text variant="metric">
          {t("productDetails:salesData.amazonSales.error")}
        </Text>
      </div>
    );
  }

  // While bundled, also wait for the formats to load: without them the mapper
  // has nothing to stack by, so the chart would briefly show the combined
  // aggregate and then snap to the per-format stacks, which contradicts the
  // bundled "stacked by format" acceptance criterion.
  if (isLoading || !sales || (isBundled && isFormatsLoading)) {
    return <Skeleton className="h-[360px] w-full rounded-xl" />;
  }

  if (sales.buckets.length === 0) {
    return (
      <AmazonSalesChartPanel>
        <EmptyChart
          data-testid="amazon-sales-unavailable"
          title={t("salesData.empty.title")}
          text={
            <Trans
              i18nKey="productDetails:salesData.empty.text"
              values={{ title: bookTitle }}
              components={{
                bookTitle: (
                  <strong className="font-semibold text-secondary-700" />
                ),
              }}
            />
          }
        />
      </AmazonSalesChartPanel>
    );
  }

  return (
    <AmazonSalesChart
      sales={sales}
      formats={formats}
      granularity={granularity}
      publishedAt={publishedAt}
      availableGranularities={availableGranularities}
      onGranularityChange={handleGranularityChange}
      events={events}
    />
  );
};
